import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { Media } from "@/components/forms/schemas/media";
import { ENRICHMENT_STATUSES } from "@/lib/enrichment";
import { artistNestedSchema } from "@/pages/artists/schemas/artist.nested.form";
import { eventAIEnrichmentSchema } from "@/pages/events/schemas/event.aienrichment.form";
import { reachField } from "@/pages/events/schemas/event.reach";
import { eventTicketingSchema } from "@/pages/events/schemas/event.ticketing.form";
import { checkVenueTba } from "@/pages/events/schemas/event.venue-tba";
import { genreNestedSchema } from "@/pages/genres/schemas/genre.nested.form";
import { geodataNestedFormSchema } from "@/pages/geodata/schemas/geodata.nested.form";
import { venueNestedSchema } from "@/pages/venues/schemas/venue.nested.form";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const STATUS_OPTIONS = ["Draft", "Complete"] as const;
export const TYPES_OPTIONS = [
  "Event",
  "Party",
  "Live",
  "Concert",
  "DJ set",
  "Festival",
  "Panigiri",
  "Other",
] as const;

const Offer = z
  .object({
    name: z.string(),
    type: z.enum(["discount", "giveaway"]),
    description: z
      .string()
      .nullish()
      .superRefine(fieldConfig({ fieldType: "textarea" })),
    status: z.enum(["InProgress", "Complete"]).default("InProgress"),
    price: z.string().nullish(),
    discount: z.coerce
      .number()
      .min(0, "Discount must be at least 0")
      .max(1, "Discount cannot be more than 1")
      .default(0)
      .superRefine(
        fieldConfig({
          inputProps: {
            min: 0,
            max: 1,
            step: 0.1,
          },
        }),
      ),
    promoCodes: z
      .array(z.string())
      .nullish()
      .default([])
      .superRefine(fieldConfig({ fieldType: "promoCodes" })),
    isActive: z.boolean().nullish(),
    promotions: z
      .array(z.string())
      .nullish()
      .superRefine(fieldConfig({ fieldType: "tagList" })),
    textOverrides: z
      .record(z.string(), z.string())
      .nullish()
      .superRefine(
        fieldConfig({
          fieldType: "textOverrides",
          inputProps: { required: false },
        }),
      ),
    ticketsToClaim: z.array(
      z.object({
        id: z.string(),
        url: z.string().url(),
        appleWalletUrl: z.string().url().or(z.literal("")).nullish(),
        googleWalletUrl: z.string().url().or(z.literal("")).nullish(),
        claimedBy: z.string().nullish(),
      }),
    ),
    appliesToTicketing: z
      .boolean()
      .default(false)
      .superRefine(
        fieldConfig({
          fieldType: "appliesToTicketing",
          inputProps: { required: false },
        }),
      ),
    referenceIds: z
      .array(z.string())
      .nullish()
      .superRefine(fieldConfig({ fieldType: "referenceIds" })),
    expiresAt: z
      .string()
      .datetime({ offset: true })
      .nullish()
      .superRefine(
        fieldConfig({ fieldType: "datetime", inputProps: { required: false } }),
      ),
    finalisedAt: z
      .string()
      .datetime({ offset: true })
      .nullish()
      .superRefine(
        fieldConfig({ fieldType: "datetime", inputProps: { required: false } }),
      ),
  })
  // Passthrough: `UpdateEventCommandHandler` writes `offers` as sent, so a key
  // this schema does not model is a key the next save deletes. Two are live:
  // `v4` (read by both BFFs to pick the offer flow — a stripped `true` came back
  // `false`) and `claimedCodes` (the codes users already claimed).
  .passthrough();

// ---------------------------------------------------------------------------
// Main form schema
// ---------------------------------------------------------------------------

// A price typed into a number input. A native input hands back a string, and an
// emptied one hands back "", so both are normalised before the number check:
// "" is no price (null), "20" is 20.
const priceInput = z.preprocess(
  (v) => (typeof v === "string" ? (v.trim() === "" ? null : Number(v)) : v),
  z.number().nullish(),
);

export const eventEditSchema = z.object({
  id: z
    .string()
    .optional()
    .readonly()
    .superRefine(fieldConfig({ inputProps: { disabled: true } })),

  type: z.enum(TYPES_OPTIONS),

  // Form-only: read from and folded back into `metadata.notificationRules.locality`
  // by the repository (see event.reach.ts).
  reach: reachField,

  name: z.string(),

  startDate: z
    .string()
    .datetime({ offset: true })
    .superRefine(fieldConfig({ fieldType: "datetime" })),

  endDate: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({
        fieldType: "datetime",
        inputProps: { required: false },
        // The X button empties the field; the PUT then carries no endDate,
        // which the Admin API stores as null (no end date).
        customData: { clearable: true },
      }),
    ),

  // When the doors open, if the source printed it. `startDate` is the show
  // time; old events (and pages that print only a doors time) carry the doors
  // time in `startDate` too. Declared because the edit form saves with a full
  // PUT: a key this stripping schema drops is a key the next save wipes.
  doorsTime: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({
        label: "Doors open",
        fieldType: "datetime",
        inputProps: { required: false },
        // Cleared like endDate: the PUT carries no doorsTime, stored as null.
        customData: { clearable: true },
      }),
    ),

  // Minimum age as printed by the source: 18 = "18+", 0 = "all ages", empty =
  // not stated. Not `z.preprocess` (AutoForm cannot walk it); AutoForm's
  // removeEmptyValues drops an emptied input before the coerce, so clearing the
  // field sends no key (stored as null) rather than coercing "" to 0.
  ageLimit: z.coerce
    .number()
    .int("Age limit must be a whole number")
    .min(0, "Age limit cannot be negative")
    .max(99, "Age limit must be below 100")
    .nullish()
    .superRefine(
      fieldConfig({
        label: "Age limit",
        description:
          "18 means 18+. 0 means all ages. Leave empty if not stated.",
        inputProps: { required: false },
      }),
    ),

  lineup: z
    .array(artistNestedSchema)
    .superRefine(fieldConfig({ fieldType: "lineup" })),

  genres: z
    .array(genreNestedSchema)
    .default([])
    .superRefine(fieldConfig({ fieldType: "genres" })),

  venue: venueNestedSchema
    .nullish()
    .superRefine(fieldConfig({ fieldType: "venueSelect" })),

  mapsUrl: z
    .string()
    .url()
    .nullish()
    .superRefine(fieldConfig({ fieldType: "mapsUrl" })),

  venueTba: z
    .boolean()
    .default(false)
    .superRefine(fieldConfig({ fieldType: "venueTba" })),

  slug: z
    .string()
    .nullish()
    .superRefine(fieldConfig({ fieldType: "slugField" })),

  about: z
    .string()
    .nullish()
    .superRefine(fieldConfig({ fieldType: "textarea" })),

  geodata: geodataNestedFormSchema,

  offers: z.array(Offer).nullish(),

  ticketing: eventTicketingSchema.nullish(),
  skipProbe: z
    .boolean()
    .nullish()
    .default(true)
    .superRefine(fieldConfig({ inputProps: { required: false } })),

  tickets: z
    .array(
      z
        .object({
          id: z
            .string()
            .nullish()
            .superRefine(fieldConfig({ inputProps: { disabled: true } })),
          name: z
            .string()
            .nullish()
            .superRefine(fieldConfig({ inputProps: { disabled: true } })),
          // Editable: `price` always equals `minPrice` (the save path syncs them).
          price: priceInput.superRefine(
            fieldConfig({ inputProps: { disabled: false } }),
          ),
          minPrice: priceInput.superRefine(
            fieldConfig({ inputProps: { disabled: false } }),
          ),
          // null = the top price is unknown.
          maxPrice: priceInput.superRefine(
            fieldConfig({ inputProps: { disabled: false } }),
          ),
          // Set by the pipeline or by the save path when a curator edits a price.
          priceCheckedAt: z
            .string()
            .nullish()
            .superRefine(fieldConfig({ inputProps: { disabled: true } })),
          // Read-only. A curator edits a URL and the prices; the currency is
          // a fact the pipeline found. Not declaring it is not
          // cosmetic: this schema reads with a stripping parse and the form
          // saves with a full PUT, so the backend would replace the whole
          // `tickets` array without the currency the first time a curator
          // saved the page.
          currency: z
            .string()
            .nullish()
            .superRefine(fieldConfig({ inputProps: { disabled: true } })),
          quantity: z
            .number()
            .nullish()
            .superRefine(fieldConfig({ inputProps: { disabled: true } })),
          url: z
            .string()
            .url()
            .superRefine(fieldConfig({ inputProps: { disabled: false } })),
          provider: z
            .string()
            .nullish()
            .superRefine(fieldConfig({ inputProps: { disabled: true } })),
          dummy: z.boolean().nullish(),
          // Editable checkbox. `true` = the seller's page says this link is sold out;
          // absent = not known. Toggling it is not a price edit, so it never stamps
          // `priceCheckedAt` (stampCuratorPriceEdits only reads the three price keys).
          soldOut: z
            .boolean()
            .nullish()
            .superRefine(fieldConfig({ inputProps: { disabled: false } })),
        })
        // Passthrough: the backend replaces the WHOLE `tickets` array on every
        // write, so a property this schema does not declare is a property the next
        // curator save deletes. `iconUrl` — Tina's own seller mark — is one of
        // them, so this is a live loss rather than a hypothetical one.
        .passthrough()
        .refine(
          // `price` is checked as well as `minPrice`: a legacy ticket may carry
          // only `price`, and the save path lets the edited one of the two win.
          (t) =>
            t.maxPrice == null ||
            [t.minPrice, t.price].every((p) => p == null || t.maxPrice! >= p),
          {
            message: "Max price must be at least the min price",
            path: ["maxPrice"],
          },
        ),
    )
    .nullish(),

  media: Media.superRefine(
    fieldConfig({
      fieldType: "media",
      customData: { collection: "Events" },
    }),
  ),

  tags: z
    .array(z.string())
    .superRefine(fieldConfig({ fieldType: "eventTags" })),

  hide: z
    .boolean()
    .default(false)
    .superRefine(fieldConfig({ inputProps: { required: false } })),

  status: z.enum(STATUS_OPTIONS).default("Complete"),

  timezone: z.string(),

  interestedIn: z.coerce.number().default(0),

  geolocation: z.object({
    latitude: z.coerce.number(),
    longitude: z.coerce.number(),
  }),

  createdBy: z
    .string()
    .nullish()
    .superRefine(fieldConfig({ inputProps: { disabled: true } })),

  createdAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(fieldConfig({ inputProps: { disabled: true } })),

  // The Tina Trace stamp, as Tina's `build_event_dto` writes it today: FLAT keys
  // on `metadata`, never a nested `metadata.tina` block (Tina #912, 2026-09-22,
  // stripped the block from the 38,301 events that had it). All read-only: this
  // is provenance, the late-dedup query matches on `url`, and the Tina Trace card
  // in the aside resolves through these keys.
  //
  // Every key is optional. An event IMS created has no stamp at all, and one a
  // refresh later touched can carry `sources` with no `id` — a required `id` here
  // failed validation and made Save do nothing.
  //
  // `jobId` is gone: it is the pre-Tina name of `runId` (0 of 300 recent events
  // carry it). An old event that still has it keeps it through `.passthrough()`,
  // and `entity-trace.ts` still reads it as the `runId` fallback.
  //
  // Passthrough: the backend FULL-REPLACES `metadata` with whatever IMS sends,
  // so a key not modelled here must still ride through. `sources` (refresh
  // provenance, R30) is deliberately not a form field — it is pipeline state,
  // never hand-edited. `notificationRules` rides through here too; its one
  // editable key, `locality`, is the top-level `reach` field above.
  metadata: z
    .object({
      id: z
        .string()
        .nullish()
        .superRefine(
          fieldConfig({
            label: "Tina item id",
            inputProps: { disabled: true },
          }),
        ),
      runId: z
        .string()
        .nullish()
        .superRefine(
          fieldConfig({ label: "Tina run id", inputProps: { disabled: true } }),
        ),
      // Not `.url()`: a stored value that fails URL parsing would block Save on a
      // field the curator cannot even edit.
      url: z
        .string()
        .nullish()
        .superRefine(
          fieldConfig({ label: "Source URL", inputProps: { disabled: true } }),
        ),
      site: z
        .string()
        .nullish()
        .superRefine(fieldConfig({ inputProps: { disabled: true } })),
      timestamp: z
        .string()
        .nullish()
        .superRefine(
          fieldConfig({ label: "Scraped at", inputProps: { disabled: true } }),
        ),
      source: z
        .string()
        .nullish()
        .superRefine(fieldConfig({ inputProps: { disabled: true } })),
      // The `signals` stage's 1-100 score. Read-only: it is an input to ranking,
      // and a hand-typed value would be indistinguishable from a computed one.
      popularity: z.coerce
        .number()
        .nullish()
        .superRefine(fieldConfig({ inputProps: { disabled: true } })),
      // Pipeline confidence — modelled explicitly (hidden, never wiped on load)
      // because a curator edit re-stamps it to 1. See withCuratorConfidence below.
      confidence: z
        .number()
        .nullish()
        .superRefine(fieldConfig({ fieldType: "hidden" })),
    })
    .passthrough()
    .nullish(),

  // Derived, never hand-listed: this was copy four of the five-member status
  // vocabulary (the sweep that centralised it caught the three column filters
  // and missed the two form schemas). A sixth `EnrichmentStatusDb` value now
  // has to be added in
  // `@/lib/enrichment` or nowhere.
  //
  // `enrichmentStatus` fieldType rather than the inferred `select`: AutoForm
  // renders a `z.enum`'s members verbatim, so the disabled dropdown read
  // `NeedsReview` while the EventSummary aside on the same screen read
  // "Needs Review". See EnrichmentStatusField for why a label map is impossible.
  enrichmentStatus: z
    .enum(ENRICHMENT_STATUSES)
    .nullish()
    .superRefine(fieldConfig({ fieldType: "enrichmentStatus" })),

  // Carried, never drawn. The aside (`EventSummary`) already renders the shared
  // `EnrichmentSummary` card for this event, and this field drew a second, identical
  // copy at the bottom of the form — same header, same status badge, same 23 bars,
  // same link. `hidden` keeps the object in form state so it still rides through
  // `PUT /events`; the zod type is unchanged and the value is never edited here.
  //
  // Not `enrichmentSummary` any more. That fieldType stays registered in
  // `injectCustomAutoFormComponents` — no schema uses it now, and the next entity
  // form that wants the card should reach for it rather than rebuild one.
  enrichment: eventAIEnrichmentSchema
    .nullish()
    .superRefine(fieldConfig({ fieldType: "hidden" })),

  // Carried, never drawn, never edited here. This schema reads with a stripping
  // parse, so without it the edit page would not know the event's group: the
  // summary's group line reads it. Every member is nullish because the
  // backend's `EventGroupRefDbDto` is (a ref from a Typesense hit can lack
  // any of them). Membership changes only through the group routes
  // (`addEvents` / `removeEvents` in `use-event-group-repository`); the event's
  // PUT keeps the stored group whatever the body says, and `update` in
  // `use-event-repository` leaves it out of the body.
  group: z
    .object({
      id: z.string().nullish(),
      name: z.string().nullish(),
      slug: z.string().nullish(),
    })
    .nullish()
    .superRefine(fieldConfig({ fieldType: "hidden" })),
});

/**
 * What the edit form validates and hands its ZodProvider. The plain
 * `eventEditSchema` stays a ZodObject for `.shape` and reads.
 */
export const eventEditSubmitSchema = eventEditSchema.superRefine(checkVenueTba);

export type EventEditFormType = z.infer<typeof eventEditSchema>;

/**
 * A curator editing an event is an explicit full-confidence signal. Bump an
 * existing pipeline confidence to 1; never add one to an event that never had it.
 *
 * Sent in the update payload so the backend — which full-replaces `metadata` with
 * whatever IMS sends — persists 1.0. This replaces the server-side re-stamp in the
 * Admin API's UpdateEventCommandHandler (branch fix/event-update-full-confidence),
 * so no backend change is needed.
 */
export function withCuratorConfidence(
  metadata: EventEditFormType["metadata"],
): EventEditFormType["metadata"] {
  if (metadata && metadata.confidence != null) {
    return { ...metadata, confidence: 1 };
  }
  return metadata;
}
